import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Filter, Download, ArrowLeft, Pencil } from 'lucide-react';
import EnhancedTable, { TableColumn } from '../../../components/common/EnhancedTable';
import ExpandableText from '../../../components/common/ExpandableText';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { formatDate } from '../../../utils/dateUtils';
import { formatTextWithLinks } from '../../../utils/formatters';
import { downloadCsv, formatTimestamp } from '../../../utils/exportUtils';
import { LotLeaseSummary } from '../../../services/supabase/lot-lease-summary';
import AddSubLeaseModal from './components/AddSubLeaseModal';
import EditSubLeaseModal from './components/EditSubLeaseModal';

export default function SubLeasesAdmin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedLease, setSelectedLease] = useState<LotLeaseSummary | null>(null);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState({
    lease_dealing: '',
  });

  const { data: leases, isLoading } = useQuery<LotLeaseSummary[]>({
    queryKey: ['admin', 'lot_lease_summary'],
    queryFn: async () => {
      console.log('Fetching lot lease summary data...');
      
      // First get the basic lot lease summary data
      const { data, error } = await supabase
        .from('lot_lease_summary')
        .select('*')
        .order('community_name', { ascending: true });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log(`Retrieved ${data?.length || 0} lot lease summary records`);

      // If we have data, fetch the additional fields from the subleases table
      if (data && data.length > 0) {
        // Get all lot IDs
        const lotIds = data.map(lease => lease.lot_id);
        console.log('Lot IDs to process:', lotIds);

        // Get the lease_lots data to map lot_id to lease_id
        const { data: leaseLotsData, error: leaseLotsError } = await supabase
          .from('lease_lots')
          .select('leaselot_lot, leaselot_lease')
          .in('leaselot_lot', lotIds);

        if (leaseLotsError) {
          console.error('Error fetching lease_lots:', leaseLotsError);
        } else if (leaseLotsData) {
          console.log(`Retrieved ${leaseLotsData.length} lease_lots records`);
          
          // Create a mapping of lot_id to lease_id
          const lotToLeaseMap = leaseLotsData.reduce((map, item) => {
            map[item.leaselot_lot] = item.leaselot_lease;
            return map;
          }, {} as Record<string, number>);

          console.log('Lot to lease mapping:', lotToLeaseMap);

          // Get all lease IDs
          const leaseIds = Object.values(lotToLeaseMap);
          console.log('Lease IDs to fetch:', leaseIds);

          // Fetch additional fields from subleases table
          const { data: subleasesData, error: subleasesError } = await supabase
            .from('subleases')
            .select('lease_id, lease_method, lease_type, lease_hmth_term, lease_dealing, lease_furth_term, lease_deal_doc')
            .in('lease_id', leaseIds);

          if (subleasesError) {
            console.error('Error fetching subleases:', subleasesError);
          } else if (subleasesData) {
            console.log(`Retrieved ${subleasesData.length} subleases records`);
            
            // Create a mapping of lease_id to sublease data
            const leaseToSubleaseMap = subleasesData.reduce((map, item) => {
              map[item.lease_id] = item;
              return map;
            }, {} as Record<number, any>);

            // Fetch rental amount data from rental_contract table
            console.log('Fetching rental contract data for lease IDs:', leaseIds);
            
            const { data: rentalData, error: rentalError } = await supabase
              .from('rental_contract')
              .select('rental_trans_id, rental_lease, rental_amount')
              .in('rental_lease', leaseIds);
              
            if (rentalError) {
              console.error('Error fetching rental data:', rentalError);
            } else {
              console.log(`Retrieved ${rentalData?.length || 0} rental contract records:`, rentalData);
            }
            
            // Create a mapping of lease_id to rental amount
            const leaseToRentalMap = rentalData ? rentalData.reduce((map, item) => {
              map[item.rental_lease] = item.rental_amount;
              return map;
            }, {} as Record<number, number>) : {};

            console.log('Lease to rental amount mapping:', leaseToRentalMap);

            // Enhance the lot lease summary data with the additional fields
            data.forEach(lease => {
              const leaseId = lotToLeaseMap[lease.lot_id];
              if (leaseId && leaseToSubleaseMap[leaseId]) {
                const subleaseData = leaseToSubleaseMap[leaseId];
                lease.lease_method = subleaseData.lease_method;
                lease.lease_type = subleaseData.lease_type;
                lease.lease_hmth_term = subleaseData.lease_hmth_term;
                lease.lease_dealing = subleaseData.lease_dealing;
                lease.lease_furth_term = subleaseData.lease_furth_term;
                lease.lease_deal_doc = subleaseData.lease_deal_doc;
              }
              
              // Add rental amount data
              if (leaseId && leaseToRentalMap[leaseId]) {
                lease.rental_amount = leaseToRentalMap[leaseId];
                console.log(`Set rental amount for lot ${lease.lot_id} (lease ${leaseId}): ${lease.rental_amount}`);
              }
            });
          }
        }
      }

      return data;
    }
  });

  // Debug logging for rental amounts
  useEffect(() => {
    if (leases) {
      console.log('Leases with rental amounts:', leases.filter(lease => lease.rental_amount).map(lease => ({
        lot_id: lease.lot_id,
        rental_amount: lease.rental_amount
      })));
    }
  }, [leases]);

  // Function to handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Function to sort leases
  const sortLeases = (leases: LotLeaseSummary[]) => {
    if (!sortField) return leases;
    
    return [...leases].sort((a, b) => {
      const aValue = a[sortField as keyof LotLeaseSummary] || '';
      const bValue = b[sortField as keyof LotLeaseSummary] || '';
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Apply search filter
  const searchFilteredLeases = leases?.filter(lease => 
    lease.lot_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (lease.holder_name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (lease.community_name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (lease.lease_dealing?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  // Apply additional filters
  const applyFilters = (leases: LotLeaseSummary[]) => {
    return leases.filter(lease => {
      // Filter by lease_dealing if filter is set
      if (filters.lease_dealing && 
          !(lease.lease_dealing?.toLowerCase() || '').includes(filters.lease_dealing.toLowerCase())) {
        return false;
      }
      return true;
    });
  };

  // Apply both filtering and sorting
  const filteredLeases = applyFilters(searchFilteredLeases || []);
  const sortedAndFilteredLeases = sortLeases(filteredLeases);

  const handleSuccess = () => {
    console.log('Invalidating queries to refresh data');
    queryClient.invalidateQueries({ queryKey: ['admin', 'lot_lease_summary'] });
  };

  // Debug logging when selecting a lease for editing
  const handleEditLease = (lease: LotLeaseSummary) => {
    console.log('Selected lease for editing:', lease);
    console.log('Rental amount:', lease.rental_amount);
    setSelectedLease(lease);
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Level 1: Page Header - Always visible */}
      <div className="sticky-header pt-2 pb-4 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Manage Subleases</h1>
              <p className="mt-1 text-sm text-gray-500">
                Add, edit, and manage property subleases
              </p>
            </div>
            <button 
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Sublease
            </button>
          </div>
        </div>
      </div>

      {/* Level 2: Controls Area - Sticky below header */}
      <div className="sticky-controls py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div className="flex gap-4 items-center flex-1">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search subleases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className={`px-4 py-2 border ${isFilterOpen ? 'border-primary bg-primary-light' : 'border-gray-300'} rounded-md hover:bg-gray-50`}
            >
              <Filter className={`h-5 w-5 ${isFilterOpen ? 'text-primary' : 'text-gray-500'}`} />
            </button>
          </div>
          
          <button
            onClick={() => {
                if (!filteredLeases?.length) return;
                
                const headers = [
                  'Community',
                  'Lot ID',
                  'Holder',
                  'Start Date',
                  'End Date',
                  'Rental Amount',
                  'Status'
                ].join(',');

                const rows = filteredLeases.map(lease => [
                  lease.community_name || '',
                  lease.lot_id || '',
                  lease.holder_name || '',
                  lease.lease_start ? formatDate(lease.lease_start) : '',
                  lease.lease_end ? formatDate(lease.lease_end) : '',
                  lease.rental_amount ? `$${lease.rental_amount}` : '',
                  lease.lot_avail ? 'Available' : 'Leased'
                ].map(value => `"${value}"`).join(','));

                const csvContent = [headers, ...rows].join('\n');
                const timestamp = formatTimestamp(new Date());
                const filename = `subleases-export-${timestamp}.csv`;
                downloadCsv(csvContent, filename);
              }}
              className="ml-4 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!filteredLeases?.length}
            >
              Export
            </button>
        </div>
        
        {isFilterOpen && (
          <div className="mt-4 p-4 bg-white border rounded-md shadow-sm">
            <h3 className="text-sm font-medium mb-3">Filters</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Lease Dealing No.
                </label>
                <input
                  type="text"
                  value={filters.lease_dealing}
                  onChange={(e) => setFilters({...filters, lease_dealing: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="Filter by lease dealing..."
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setFilters({lease_dealing: ''})}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced table with fixed header and horizontal scrolling */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden mt-4">
        <EnhancedTable
          columns={[
            { 
              id: 'actions', 
              header: 'Edit', 
              accessor: (row) => (
                <button 
                  onClick={() => handleEditLease(row)}
                  className="text-primary hover:text-primary-dark"
                >
                  <Pencil className="h-5 w-5" />
                </button>
              ),
              width: 'narrow'
            },
            { 
              id: 'lot_id', 
              header: 'Lot ID', 
              accessor: (row) => row.lot_id,
              width: 'narrow',
              sortable: true
            },
            { 
              id: 'lease_dealing', 
              header: 'Lease Dealing No.', 
              accessor: (row) => row.lease_dealing || '-',
              width: 'medium',
              sortable: true
            },
            { 
              id: 'holder_name', 
              header: 'Leaseholder', 
              accessor: (row) => (
                <div className="truncate" title={row.holder_name || 'Unassigned'}>
                  {formatTextWithLinks(row.holder_name || 'Unassigned')}
                </div>
              ),
              width: 'medium',
              sortable: true
            },
            { 
              id: 'lease_start', 
              header: 'Start Date', 
              accessor: (row) => row.lease_start ? formatDate(row.lease_start) : '-',
              width: 'narrow'
            },
            { 
              id: 'lease_end', 
              header: 'End Date', 
              accessor: (row) => row.lease_end ? formatDate(row.lease_end) : '-',
              width: 'narrow'
            },
            { 
              id: 'lot_perm_use', 
              header: 'Permitted Use', 
              accessor: (row) => (
                <ExpandableText 
                  text={(row.lot_perm_use || 'Not specified')} 
                  maxLength={50} 
                  className="text-sm"
                />
              ),
              width: 'wide'
            },
            { 
              id: 'rental_amount', 
              header: 'Rental Amount', 
              accessor: (row) => row.rental_amount ? `$${row.rental_amount}` : 'Not specified',
              width: 'narrow',
              align: 'right'
            },
            { 
              id: 'lease_rent_freq', 
              header: 'Rental Frequency', 
              accessor: (row) => row.lease_rent_freq || 'Not specified',
              width: 'narrow'
            },
            { 
              id: 'lease_deal_doc', 
              header: 'Deal Document', 
              accessor: (row) => (
                <ExpandableText 
                  text={row.lease_deal_doc || 'Not specified'} 
                  maxLength={50} 
                  className="text-sm"
                />
              ),
              width: 'wide'
            }
          ]}
          data={sortedAndFilteredLeases}
          keyField="lot_id"
          width="2800px"
          maxHeight="calc(100vh - 16rem)"
          loading={isLoading}
          emptyMessage="No leases found"
          defaultSort={sortField ? { field: sortField, direction: sortDirection } : undefined}
          onSort={(newSortConfig) => {
            setSortField(newSortConfig.field);
            setSortDirection(newSortConfig.direction);
          }}
          horizontalScroll={true}
        />
      </div>

      <AddSubLeaseModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleSuccess}
      />

      {selectedLease && (
        <EditSubLeaseModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedLease(null);
          }}
          onSuccess={handleSuccess}
          sublease={selectedLease}
        />
      )}
    </div>
  );
}
